import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import MatchListView from '../lists/MatchListView';
import AddRoundModal from '../AddRoundModal';
import { applyTieFormatToRounds } from '../../generators/tieFormats';
import { isEnabled, TOURNAMENT_ENTITIES } from '../../../tournaments/access';

/**
 * Cross-Group Duals result view. Renders the schedule directly — no
 * Teams/Matches toggle or team-roster panel, since every roster name it
 * would show is already visible on the Home/Away columns of the match list
 * itself. Cross-Group only ever produces ONE match per tie; MLP Slate is
 * layered in as a Tie Format (see tieFormats.js) exactly the same way it
 * would be for Round Robin or any other structure, not something special to
 * this format.
 */
const CrossGroupView = ({
  schedule,
  tieFormat,
  tieOverrides,
  matchOrderOverrides,
  reorderRow,
  courts,
  sportType,
  tournamentMatchSettings,
  roundOverrides,
  rowOverrides,
  applyRoundOverride,
  applyRowOverride,
  filters,
  currentUserId,
  onClearFilters,
  tournamentVO,
  startScoring,
  openLiveGame,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  addRound,
  addMatch,
  fetchAllParticipants,
  participantType,
}) => {
  const { theme } = useTheme();
  const [addRoundOpen, setAddRoundOpen] = useState(false);
  const displayedRounds = useMemo(
    () => applyTieFormatToRounds(schedule.rounds, tieFormat, tieOverrides),
    [schedule.rounds, tieFormat, tieOverrides]
  );
  const canAddRound = !!addRound && isEnabled(currentUserId, TOURNAMENT_ENTITIES.ROUND_ADD, tournamentVO);

  return (
    <div>
      {canAddRound && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => setAddRoundOpen(true)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
          >
            <Plus size={13} />
            Add Round
          </button>
        </div>
      )}
      <MatchListView
        rounds={displayedRounds}
        title="Full Schedule — All Ties"
        orderOverrides={matchOrderOverrides?.matches}
        onReorderRow={(roundNumber, order) => reorderRow('matches', roundNumber, order)}
        courts={courts}
        sportType={sportType}
        tournamentMatchSettings={tournamentMatchSettings}
        roundOverrides={roundOverrides?.matches}
        rowOverrides={rowOverrides?.matches}
        onApplyRoundOverride={(roundNumber, override, clearRowKeys, rowLiveDataIds) => applyRoundOverride('matches', roundNumber, override, clearRowKeys, rowLiveDataIds)}
        onApplyRowOverride={(rowKey, override, liveData) => applyRowOverride('matches', rowKey, override, liveData)}
        filters={filters}
        currentUserId={currentUserId}
        onClearFilters={onClearFilters}
        tournamentVO={tournamentVO}
        startScoring={startScoring}
        openLiveGame={openLiveGame}
        canManageThisSchedule={canManageThisSchedule}
        updateMatchSchedule={updateMatchSchedule}
        deleteMatchRow={deleteMatchRow}
        assignMatchParticipants={assignMatchParticipants}
        addMatch={addMatch}
        fetchAllParticipants={fetchAllParticipants}
        participantType={participantType}
      />
      {canAddRound && (
        <AddRoundModal isOpen={addRoundOpen} onClose={() => setAddRoundOpen(false)} onSave={addRound} />
      )}
    </div>
  );
};

export default CrossGroupView;
